import { useLayoutStrategyConfig } from '@src/hooks/useLayoutStrategyConfig';
import { useWindowingConfig } from '@src/hooks/useWindowingConfig';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { computed, configure, runInAction } from 'mobx';
import { useContext, useEffect, useState } from 'react';

import { RootStore } from './RootStore';
import { StoreContext } from './RootStoreContext';

import type { FeatureFlags } from './FeatureFlagsStore';
import type { ReactNode } from 'react';

// MobX configuration
// We want to enforce actions in development to catch accidental state changes
// but given how pervasive MobX is in our codebase, we can't enforce actions in production
// without a lot of refactoring.
// if (process.env.NODE_ENV === 'development') {
//   configure({
//     enforceActions: 'observed',
//   });
// } else {
configure({
  enforceActions: 'never',
});
// }

// Re-exported so every existing `import { StoreContext } from './StoreContext'`
// keeps working; the context itself lives in `RootStoreContext` so a consumer
// that only reads it does not pull the whole store graph in with it.
export { StoreContext };

interface StoreProviderProps {
  children: ReactNode;
  initialStore?: RootStore;
}

export const StoreProvider = ({
  children,
  initialStore,
}: StoreProviderProps) => {
  // useState with lazy initializer ensures the RootStore is created once and
  // survives re-renders caused by hooks like useFlagsCombination
  // re-render would create a fresh RootStore, losing repository initialization
  const [store] = useState(() => initialStore || new RootStore());
  const flags = useFlagsCombination(store.userIdentityStore.currentRole);

  // Sync resolved flags into the MobX store so that deeply nested components
  // can read them without each calling useFlagsCombination individually.
  useEffect(() => {
    runInAction(() => {
      store.featureFlagsStore.setFlags(flags);
    });
  }, [flags, store]);

  // Forward layout strategy configuration to the editor store.
  // Centralised here so all providers (VideoCreatorStore, ClipVideoCreatorAdapter)
  // share the same layout config without duplicating wiring.
  useLayoutStrategyConfig(store.editorStore, flags);
  useWindowingConfig(store.editorStore, flags);

  return (
    <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  );
};

// Base store hook
export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }

  // if (process.env.REACT_APP_ENVIRONMENT === 'development') {
  //   const callingModule = module.parent?.filename;
  //   if (!callingModule?.includes('StoreContext')) {
  //     console.warn(
  //       'Warning: useStore() was called directly. This may cause unnecessary re-renders. ' +
  //         'Use specific store hooks like useUserIdentity() instead. ' +
  //         'See StoreContext.tsx for more details.',
  //     );
  //   }
  // }

  return context;
};

// Store Hooks
//
// These hooks are optimized to prevent unnecessary re-renders by using granular observers.
// Each hook wraps its return value in observer(() => ...)() which tells MobX to:
// 1. Only track changes to the specific store/data being returned
// 2. Only trigger re-renders when that specific data changes
// 3. Ignore changes to unrelated parts of the store tree
//
// Example:
// - Bad:  const { userIdentityStore } = useStore();
//         // Will re-render on ANY store change
//
// - Good: const userIdentity = useUserIdentity();
//         // Only re-renders when userIdentityStore changes

export const useFeatureFlags = (): Partial<FeatureFlags> => {
  const store = useStore();
  return computed(() => store.featureFlagsStore.flags).get();
};

export const useUserIdentity = () => {
  const store = useStore();
  return computed(() => store.userIdentityStore).get();
};

export const useAnalytics = () => {
  const store = useStore();
  return computed(() => store.analyticsStore).get();
};

export const useTemplatedPrompts = () => {
  const store = useStore();
  return computed(() => store.templatedPromptsStore).get();
};

export const useDatoClient = () => {
  const store = useStore();
  return computed(() => store.datoClientStore).get();
};

export const useTranscription = () => {
  const store = useStore();
  return computed(() => store.transcriptionStore).get();
};

export const useTranslationStore = () => {
  const store = useStore();
  return computed(() => store.translationStore).get();
};

export const useLanguageStore = () => {
  const store = useStore();
  return computed(() => store.languageStore).get();
};

export const useTranslationHandlerStore = () => {
  const store = useStore();
  return computed(() => store.translationHandlerStore).get();
};

export const useDubbingStore = () => {
  const store = useStore();
  return computed(() => store.dubbingStore).get();
};

export const useDashboard = () => {
  const store = useStore();
  return computed(() => store.dashboardStore).get();
};

export const useUserStoryUploadFlow = () => {
  const store = useDashboard();
  return computed(() => store.userStoryUploadFlowStore).get();
};

export const useStoryManagerView = () => {
  const store = useDashboard();
  return computed(() => store.storyManagerViewStore).get();
};

export const useEditor = () => {
  const store = useStore();
  return computed(() => store.editorStore).get();
};

export const useVideoSource = () => {
  const store = useStore();
  return computed(() => store.videoSourceStore).get();
};

export const useDatoPerson = () => {
  const store = useStore();
  return computed(() => store.datoPersonStore).get();
};

export const useLayoutStore = () => {
  const store = useStore();
  return computed(() => store.layoutStore).get();
};

export const useKaraokeStore = () => {
  const store = useStore();
  return computed(() => store.karaokeStore).get();
};

export const useWriteQueue = () => {
  const store = useStore();
  return computed(() => store.writeQueueStore).get();
};

export const useClipPreview = () => {
  const store = useStore();
  return computed(() => store.clipPreviewStore).get();
};

export const useStudioHandoff = () => {
  const store = useStore();
  return computed(() => store.studioHandoffStore).get();
};

export const usePublishTrackingStore = () => {
  const store = useStore();
  return computed(() => store.publishTrackingStore).get();
};

export const usePublishRecordsStore = () => {
  const store = useStore();
  return computed(() => store.publishRecordsStore).get();
};

export const useWhatsNew = () => {
  const store = useStore();
  return computed(() => store.whatsNewStore).get();
};

export const useContentStudioUI = () => {
  const store = useStore();
  return computed(() => store.contentStudioUIStore).get();
};

export const useDistributionChannels = () => {
  const store = useStore();
  return computed(() => store.distributionChannelsStore).get();
};

export const useTranslationMemoryStore = () => {
  const store = useStore();
  return computed(() => store.translationMemoryStore).get();
};

export const useTranslationMemoryEntryStore = () => {
  const store = useStore();
  return computed(() => store.translationMemoryEntryStore).get();
};

export const useTranslationMemoryImportStore = () => {
  const store = useStore();
  return computed(() => store.translationMemoryImportStore).get();
};

export const useFolderStructure = () => {
  const store = useStore();
  return computed(() => store.folderStructureStore).get();
};
